import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';

import { broadcastApi, scheduleBlockApi } from '../../api/examApi';
import { DashboardLayout } from '../../components/layout';
import { parseApiError } from '../../lib/apiErrors';

const DAYS = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
];

const START_HOUR = 9;
const SLOT_COUNT = 24; // 09:00–21:00 in 30-minute slots

const slotStartTime = (index) => {
  const totalMinutes = START_HOUR * 60 + index * 30;
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const timeToSlotIndex = (time) => {
  const [h, m] = time.split(':').map(Number);
  return (h * 60 + m - START_HOUR * 60) / 30;
};

const formatSlotLabel = (index) => {
  const totalMinutes = START_HOUR * 60 + index * 30;
  const h24 = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  const period = h24 >= 12 ? 'PM' : 'AM';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}${m === 0 ? '' : ':' + String(m).padStart(2, '0')} ${period}`;
};

const CAMPUS_LABEL = { ardee: 'Ardee City', arcadia: 'Arcadia' };
const CAMPUS_OPTIONS = [
  { value: 'ardee', label: 'Ardee City' },
  { value: 'arcadia', label: 'Arcadia' },
];
const DAY_LABEL = DAYS.reduce((acc, d) => ({ ...acc, [d.key]: d.label }), {});
const DAY_LABEL_FULL = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
};

function ScheduleBlockFormModal({ mode, initial, groups, onClose, onSubmit, onRequestDelete }) {
  const [groupId, setGroupId] = useState(initial.group_id || '');
  const [subject, setSubject] = useState(initial.subject || '');
  const [campus, setCampus] = useState(initial.campus || '');
  const [note, setNote] = useState(initial.note || '');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await onSubmit({
        group_id: groupId,
        subject: subject.trim(),
        campus,
        note: note.trim() || null,
      });
      onClose();
    } catch (err) {
      setError(parseApiError(err, 'Failed to save this class block').message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl dark:bg-slate-800"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-heading font-bold text-slate-900 dark:text-white">
            {mode === 'edit' ? 'Edit Class' : 'New Class'}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
            <X size={20} />
          </button>
        </div>

        <div className="mb-4 rounded-md bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-600 dark:text-slate-300">
          {DAY_LABEL_FULL[initial.day_of_week]}, {initial.start_time}–{initial.end_time}
          {mode === 'create' && (
            <span className="block text-xs text-slate-400 mt-0.5">
              To change the day or time, cancel and reselect on the grid.
            </span>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Group</label>
              <select
                value={groupId}
                onChange={(e) => setGroupId(e.target.value)}
                required
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                data-testid="block-group-select"
              >
                <option value="">Select group…</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Campus</label>
              <select
                value={campus}
                onChange={(e) => setCampus(e.target.value)}
                required
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                data-testid="block-campus-select"
              >
                <option value="">Select campus…</option>
                {CAMPUS_OPTIONS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
              placeholder="e.g. Physics"
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
              data-testid="block-subject-input"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Note (optional)
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
              data-testid="block-note-input"
            />
          </div>

          {error && (
            <p className="text-sm text-rose-500" data-testid="block-form-error">
              {error}
            </p>
          )}

          <div className="flex items-center justify-between gap-3 pt-1">
            {mode === 'edit' ? (
              <button
                type="button"
                onClick={onRequestDelete}
                className="rounded-md px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                data-testid="block-delete-btn"
              >
                Delete
              </button>
            ) : (
              <span />
            )}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving || !groupId || !campus || !subject.trim()}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-light disabled:opacity-50"
                data-testid="block-form-submit"
              >
                {saving ? 'Saving…' : mode === 'edit' ? 'Save Changes' : 'Create'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function DeleteBlockConfirmModal({ block, deleting, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 p-4" onClick={onCancel}>
      <div
        className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl dark:bg-slate-800"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-2 text-lg font-heading font-bold text-slate-900 dark:text-white">Delete this class?</h3>
        <p className="mb-5 text-sm text-slate-600 dark:text-slate-300">
          This permanently removes the {DAY_LABEL_FULL[block.day_of_week]} {block.start_time}–{block.end_time}{' '}
          {block.subject} class for {block.group_name}. This cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={deleting}
            className="rounded-md px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={deleting}
            className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
            data-testid="confirm-delete-block"
          >
            {deleting ? 'Deleting…' : 'Delete Class'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function FacultySchedulePage() {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);

  // Two-click range selection: first click sets pendingStart, a second
  // valid click in the same day commits pendingRange (inclusive slot
  // indices). pendingRange stays set until the detail form consumes and
  // clears it (on submit or cancel).
  const [pendingStart, setPendingStart] = useState(null);
  const [pendingRange, setPendingRange] = useState(null);

  // Non-null opens the form in edit mode instead of create mode.
  const [editingBlock, setEditingBlock] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleCellClick = useCallback(
    (day, slotIndex) => {
      if (pendingRange) return;
      if (!pendingStart) {
        setPendingStart({ day, slotIndex });
        return;
      }
      if (pendingStart.day === day && pendingStart.slotIndex === slotIndex) {
        // Clicking the pending-start cell again cancels the selection.
        setPendingStart(null);
        return;
      }
      if (pendingStart.day !== day) {
        // A different day resets to a fresh start rather than erroring.
        setPendingStart({ day, slotIndex });
        return;
      }
      // Same day, second click: commit the range, swapping silently if the
      // second click landed before the first.
      const startIdx = Math.min(pendingStart.slotIndex, slotIndex);
      const endIdx = Math.max(pendingStart.slotIndex, slotIndex);
      setPendingRange({ day, startIdx, endIdx });
      setPendingStart(null);
    },
    [pendingStart, pendingRange]
  );

  const handleCancelSelection = useCallback(() => {
    setPendingStart(null);
    setPendingRange(null);
  }, []);

  const handleBlockClick = useCallback((block) => {
    // A block click is a distinct action from range selection — it never
    // contributes to a pending start/end, and always opens edit mode.
    setPendingStart(null);
    setPendingRange(null);
    setEditingBlock(block);
  }, []);

  const loadBlocks = useCallback(() => {
    setLoading(true);
    scheduleBlockApi
      .list()
      .then(({ data }) => setBlocks(data))
      .catch(() => toast.error('Failed to load schedule'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadBlocks();
  }, [loadBlocks]);

  useEffect(() => {
    broadcastApi
      .getEligibleGroups()
      .then(({ data }) => setGroups(data))
      .catch(() => toast.error('Failed to load groups'));
  }, []);

  // Derives the form's mode + prefilled fields from whichever of
  // pendingRange / editingBlock is currently set (mutually exclusive).
  const formConfig = useMemo(() => {
    if (pendingRange) {
      return {
        mode: 'create',
        initial: {
          day_of_week: pendingRange.day,
          start_time: slotStartTime(pendingRange.startIdx),
          end_time: slotStartTime(pendingRange.endIdx + 1),
          group_id: '',
          subject: '',
          campus: '',
          note: '',
        },
      };
    }
    if (editingBlock) {
      return {
        mode: 'edit',
        initial: {
          day_of_week: editingBlock.day_of_week,
          start_time: editingBlock.start_time,
          end_time: editingBlock.end_time,
          group_id: editingBlock.group_id,
          subject: editingBlock.subject,
          campus: editingBlock.campus,
          note: editingBlock.note || '',
        },
      };
    }
    return null;
  }, [pendingRange, editingBlock]);

  const handleFormClose = useCallback(() => {
    setPendingRange(null);
    setEditingBlock(null);
  }, []);

  const handleFormSubmit = useCallback(
    async (payload) => {
      if (formConfig?.mode === 'edit') {
        await scheduleBlockApi.update(editingBlock.id, payload);
        toast.success('Class updated');
      } else {
        await scheduleBlockApi.create({
          ...payload,
          day_of_week: formConfig.initial.day_of_week,
          start_time: formConfig.initial.start_time,
          end_time: formConfig.initial.end_time,
        });
        toast.success('Class scheduled');
      }
      loadBlocks();
    },
    [formConfig, editingBlock, loadBlocks]
  );

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    try {
      await scheduleBlockApi.remove(deleteTarget.id);
      toast.success('Class removed');
      setDeleteTarget(null);
      setEditingBlock(null);
      loadBlocks();
    } catch (err) {
      toast.error(parseApiError(err, 'Failed to delete this class').message);
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, loadBlocks]);

  // Every slot covered by an existing block, so plain empty cells are
  // skipped there and the block itself renders as one merged cell.
  const coveredSlots = useMemo(() => {
    const set = new Set();
    for (const block of blocks) {
      const start = timeToSlotIndex(block.start_time);
      const end = timeToSlotIndex(block.end_time);
      for (let i = start; i < end; i++) {
        set.add(`${block.day_of_week}|${i}`);
      }
    }
    return set;
  }, [blocks]);

  return (
    <DashboardLayout title="Schedule">
      <div className="animate-fade-in space-y-6">
        <div>
          <h2 className="text-2xl font-heading font-bold text-slate-900 dark:text-white">Weekly Schedule</h2>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Your standing weekly class blocks, 09:00–21:00
          </p>
        </div>

        {groups.length === 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/10 dark:text-amber-300">
            You're not linked to any group yet. Ask an admin to assign you to a group before
            scheduling a class.
          </div>
        )}

        {pendingStart && !pendingRange && (
          <div
            className="flex items-center justify-between gap-3 rounded-md border border-primary/30 bg-primary/5 dark:bg-primary/10 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-200"
            data-testid="pending-selection-banner"
          >
            <span>
              Selecting start: <strong>{DAY_LABEL[pendingStart.day]}</strong>{' '}
              {formatSlotLabel(pendingStart.slotIndex)} — click another slot in the same day to set
              the end.
            </span>
            <button
              type="button"
              onClick={handleCancelSelection}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-slate-500 hover:bg-slate-200/60 dark:text-slate-400 dark:hover:bg-slate-700"
              data-testid="cancel-selection"
            >
              <X size={14} /> Cancel
            </button>
          </div>
        )}

        {loading ? (
          <div className="animate-pulse h-96 rounded-lg bg-slate-100 dark:bg-slate-800" />
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
            <div
              className="grid min-w-[760px]"
              style={{
                gridTemplateColumns: '72px repeat(7, minmax(0, 1fr))',
                gridTemplateRows: `36px repeat(${SLOT_COUNT}, 32px)`,
              }}
              data-testid="schedule-grid"
            >
              {/* Header row */}
              <div
                className="bg-slate-50 dark:bg-slate-900 border-b border-r border-slate-200 dark:border-slate-700"
                style={{ gridColumn: 1, gridRow: 1 }}
              />
              {DAYS.map((day, dayIdx) => (
                <div
                  key={day.key}
                  className="flex items-center justify-center text-xs font-semibold text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-900 border-b border-l border-slate-200 dark:border-slate-700"
                  style={{ gridColumn: dayIdx + 2, gridRow: 1 }}
                  data-testid={`day-header-${day.key}`}
                >
                  {day.label}
                </div>
              ))}

              {/* Hour labels, one per 2 slots */}
              {Array.from({ length: SLOT_COUNT }).map((_, slotIdx) =>
                slotIdx % 2 === 0 ? (
                  <div
                    key={`time-${slotIdx}`}
                    className="bg-slate-50 dark:bg-slate-900 border-r border-slate-200 dark:border-slate-700 text-[11px] text-slate-400 dark:text-slate-500 pl-1.5 pt-0.5"
                    style={{ gridColumn: 1, gridRow: `${slotIdx + 2} / span 2` }}
                  >
                    {formatSlotLabel(slotIdx)}
                  </div>
                ) : null
              )}

              {/* Empty clickable cells */}
              {DAYS.map((day, dayIdx) =>
                Array.from({ length: SLOT_COUNT }).map((_, slotIdx) => {
                  const key = `${day.key}|${slotIdx}`;
                  if (coveredSlots.has(key)) return null;

                  const isPendingStart =
                    pendingStart && pendingStart.day === day.key && pendingStart.slotIndex === slotIdx;
                  const isInPendingRange =
                    pendingRange &&
                    pendingRange.day === day.key &&
                    slotIdx >= pendingRange.startIdx &&
                    slotIdx <= pendingRange.endIdx;
                  const isHighlighted = isPendingStart || isInPendingRange;

                  return (
                    <div
                      key={`cell-${key}`}
                      onClick={() => handleCellClick(day.key, slotIdx)}
                      className={`cursor-pointer border-b border-l transition-colors ${
                        isHighlighted
                          ? 'bg-primary/40 border-primary/50 dark:bg-primary/50'
                          : 'border-slate-100 dark:border-slate-700/60 hover:bg-primary/10 dark:hover:bg-primary/20'
                      }`}
                      style={{ gridColumn: dayIdx + 2, gridRow: slotIdx + 2 }}
                      data-testid={`cell-${day.key}-${slotStartTime(slotIdx)}`}
                      data-selected={isHighlighted ? 'true' : undefined}
                    />
                  );
                })
              )}

              {/* Existing blocks, rendered as one merged cell each */}
              {blocks.map((block) => {
                const dayIdx = DAYS.findIndex((d) => d.key === block.day_of_week);
                if (dayIdx === -1) return null;
                const start = timeToSlotIndex(block.start_time);
                const end = timeToSlotIndex(block.end_time);
                return (
                  <div
                    key={block.id}
                    onClick={() => handleBlockClick(block)}
                    className="cursor-pointer m-0.5 rounded-md bg-primary/90 hover:bg-primary text-white px-2 py-1 text-[11px] leading-tight overflow-hidden transition-colors flex flex-col justify-center"
                    style={{ gridColumn: dayIdx + 2, gridRow: `${start + 2} / ${end + 2}` }}
                    data-testid={`block-${block.id}`}
                  >
                    <div className="font-semibold truncate">{block.group_name}</div>
                    <div className="truncate">{block.subject}</div>
                    <div className="truncate text-white/80">
                      {CAMPUS_LABEL[block.campus] || block.campus}
                    </div>
                    <div className="truncate text-white/80">
                      {block.start_time}–{block.end_time}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {formConfig && (
        <ScheduleBlockFormModal
          mode={formConfig.mode}
          initial={formConfig.initial}
          groups={groups}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
          onRequestDelete={() => setDeleteTarget(editingBlock)}
        />
      )}

      {deleteTarget && (
        <DeleteBlockConfirmModal
          block={deleteTarget}
          deleting={deleting}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </DashboardLayout>
  );
}
